import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, User, Settings, Home, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { resolveRoleLabel } from "../lib/roleLabel";

const ROLE_BADGE = {
  admin: "bg-purple-100 text-purple-700",
  bcm_manager: "bg-blue-100 text-blue-700",
  it_drp_manager: "bg-cyan-100 text-cyan-700",
  process_manager: "bg-emerald-100 text-emerald-700",
  app_manager: "bg-sky-100 text-sky-700",
  it_function_manager: "bg-sky-100 text-sky-700",
  end_user: "bg-emerald-100 text-emerald-700",
  business_user: "bg-emerald-100 text-emerald-700",
};

/** App-wide top-right user profile widget (name, email, role badge + dropdown).
 *  Self-contained so it can be dropped into any page header. */
export default function UserProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const color = ROLE_BADGE[user?.role] || "bg-slate-100 text-slate-700";
  const roleLabel = resolveRoleLabel(user);
  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="topbar-profile-trigger" className="flex items-center gap-2 p-1 rounded-lg hover:bg-slate-100 transition-colors">
          <div className="w-8 h-8 rounded-full bg-sky-700 flex items-center justify-center shadow-sm shrink-0">
            <User className="w-4 h-4 text-white" />
          </div>
          <div className="hidden xl:block text-left min-w-0 max-w-[160px]">
            <p className="text-sm font-medium text-slate-900 truncate leading-tight">
              {user?.name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email}
            </p>
            <p className="text-[11px] text-slate-500 truncate leading-tight">{user?.email}</p>
          </div>
          <span data-testid="topbar-role-badge" className={`hidden sm:inline-block px-2 py-0.5 rounded text-[11px] font-medium ${color}`}>
            {roleLabel}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
          <p className="text-xs text-slate-500">{user?.email}</p>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${color}`}>{roleLabel}</span>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings" className="cursor-pointer"><Settings className="w-4 h-4 mr-2" />Settings</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/dashboard" className="cursor-pointer"><Home className="w-4 h-4 mr-2" />Home</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
          <LogOut className="w-4 h-4 mr-2" />Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
